// src/components/core/Breadcrumbs.jsx
/**
 * Unified Breadcrumbs component for the entire application
 * This component combines the functionality of both the core and UI Breadcrumbs components
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Breadcrumbs as MuiBreadcrumbs,
  Typography,
  Link,
  Box,
  useTheme,
  alpha,
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  Home as HomeIcon,
} from '@mui/icons-material';

/**
 * Standardized breadcrumbs component for navigation throughout the application
 * Supports automatic generation from paths or custom breadcrumb arrays
 * 
 * @returns {JSX.Element} Breadcrumbs component
 */
const Breadcrumbs = ({
  // Breadcrumb data
  items = [],
  current,
  
  // Paths and navigation
  homePath = '/dashboard',
  homeLabel = 'Home',
  rootPath,
  rootLabel,
  parentLink,         // Compatibility with UI version
  parentLabel,        // Compatibility with UI version
  additionalCrumbs,   // Compatibility with UI version
  
  // Display options
  showHome = true,
  maxItems = 4,
  separator = <NavigateNextIcon fontSize="small" />,
  
  // Style customization
  variant = 'standard',
  size = 'medium',
  sx = {},
  ...rest
}) => {
  const theme = useTheme();
  const location = useLocation();
  
  // Calculate sizes based on size prop
  const getFontSize = () => {
    switch (size) {
      case 'small': return '0.75rem';
      case 'large': return '0.95rem';
      case 'medium':
      default: return '0.85rem';
    }
  };
  
  const fontSize = getFontSize();
  
  // Generate breadcrumbs if not provided
  const getBreadcrumbItems = () => {
    // If direct items array is provided, use it
    if (items && items.length > 0) {
      return items;
    }
    
    // Support for the UI version's additionalCrumbs format
    if (additionalCrumbs && additionalCrumbs.length > 0) {
      return additionalCrumbs.map(crumb => ({
        path: crumb.link,
        label: crumb.label
      }));
    }
    
    // Support for the UI version's parentLink/parentLabel parameters
    if (parentLink && parentLabel) {
      return [{
        path: parentLink,
        label: parentLabel
      }];
    }
    
    // If we have a current item but no path structure, just show home > current
    if (current && !rootPath) {
      return [];
    }
    
    // If we have a root path/label, use those for a two-level breadcrumb
    if (rootPath && rootLabel) {
      return [{
        path: rootPath,
        label: rootLabel
      }];
    }
    
    // Fallback to empty array - will just show Home > Current
    return [];
  };
  
  // Get style variant
  const getVariantStyle = () => {
    switch (variant) {
      case 'compact':
        return {
          '& .MuiBreadcrumbs-separator': {
            mx: 0.5,
          },
          '& .MuiBreadcrumbs-li': {
            mx: 0,
          }
        };
      
      case 'elevated': 
        return {
          p: 1.5,
          borderRadius: theme.shape.borderRadius,
          backgroundColor: theme.palette.mode === 'dark' 
            ? alpha(theme.palette.background.paper, 0.6)
            : alpha(theme.palette.background.paper, 0.7),
          boxShadow: theme.shadows[1],
        };
        
      case 'standard':
      default:
        return {};
    }
  };
  
  // Get breadcrumb items
  const breadcrumbItems = getBreadcrumbItems();
  
  return (
    <MuiBreadcrumbs
      separator={separator}
      aria-label="breadcrumb"
      maxItems={maxItems}
      sx={{
        mb: 2,
        ...getVariantStyle(),
        ...sx,
      }}
      {...rest}
    >
      {/* Home */}
      {showHome && (
        <Link
          underline="hover"
          color="inherit"
          component={RouterLink}
          to={homePath}
          sx={{
            display: 'flex',
            alignItems: 'center',
            fontSize,
            '&:hover': {
              color: theme.palette.primary.main,
            }
          }}
        >
          <HomeIcon sx={{ mr: 0.5, fontSize: '1.2em' }} />
          {homeLabel}
        </Link>
      )}
      
      {/* Middle breadcrumbs */}
      {breadcrumbItems.map((item, index) => (
        <Link
          key={index}
          underline="hover"
          color="inherit"
          component={RouterLink}
          to={item.path}
          sx={{
            fontSize,
            '&:hover': {
              color: theme.palette.primary.main,
            }
          }}
        >
          {item.label}
        </Link>
      ))}
      
      {/* Current page */}
      {current && (
        <Typography
          color="text.primary"
          sx={{
            fontSize,
            fontWeight: 500,
          }}
        >
          {current}
        </Typography>
      )}
    </MuiBreadcrumbs>
  );
};

Breadcrumbs.propTypes = {
  // Core data properties
  items: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  current: PropTypes.string,
  
  // Navigation paths from core version
  homePath: PropTypes.string,
  homeLabel: PropTypes.string,
  rootPath: PropTypes.string,
  rootLabel: PropTypes.string,
  
  // Navigation paths from UI version (for compatibility)
  parentLink: PropTypes.string,
  parentLabel: PropTypes.string,
  additionalCrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      link: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  
  // Display options
  showHome: PropTypes.bool,
  maxItems: PropTypes.number,
  separator: PropTypes.node,
  
  // Style customization
  variant: PropTypes.oneOf(['standard', 'compact', 'elevated']),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  sx: PropTypes.object,
};

export default Breadcrumbs;